import { useState, useEffect } from 'react'
import { Form, Button, Row, Col, Alert, Table, Badge } from 'react-bootstrap'
import { useQuery } from 'react-query'
import api from '../../services/api'
import { getDisplaySerieFolio } from '../../utils/invoiceDisplay'
import { formatDateOnlyEsMX, getTodayDateOnlyMx } from '../../utils/apiDateFormat'
import { formatCurrency } from '../../utils/formatters'
import {
  FORMA_PAGO_SAT_OPTIONS_PAGO,
  formaPagoRequiereCuentaBancaria,
  formaPagoToPaymentMethod,
  paymentMethodToFormaPago,
} from '../../constants/formaPagoSat'

const PaymentForm = ({ payment, onSubmit, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    tipo_pago: 'ingreso',
    cliente: '',
    payment_date: getTodayDateOnlyMx(),
    amount: 0,
    reference: '',
    forma_pago: '03',
    payment_method: 'TRANSFER',
    bank_account: '',
    estado: 'pendiente',
    moneda: 'MXN',
    tipo_cambio: '',
    aplicaciones: []
  })

  const [errors, setErrors] = useState({})
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [invoiceAmounts, setInvoiceAmounts] = useState({})

  // Obtener clientes
  const { data: clients } = useQuery(
    'clients-for-payment',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )

  // Obtener cuentas bancarias
  const { data: bankAccounts } = useQuery(
    'bank-accounts-for-payment',
    async () => {
      const response = await api.get('/financial/bank-accounts/?is_active=true')
      return response.data.results || response.data || []
    }
  )

  const { data: catalogoMonedas } = useQuery(
    'catalogo-monedas-sat-payment-form',
    async () => {
      const response = await api.get('/financial/invoices/catalogo-moneda/')
      return response.data.results || response.data || []
    },
    { staleTime: 600000 }
  )

  // Obtener facturas pendientes según tipo de pago
  const { data: invoicesData, isLoading: loadingInvoices, error: invoicesError } = useQuery(
    ['pending-invoices', formData.cliente, formData.tipo_pago],
    async () => {
      if (!formData.cliente || !formData.tipo_pago) return []
      
      try {
        // Usar el nuevo endpoint que filtra según tipo de pago
        const response = await api.get(`/financial/payments/facturas_pendientes/`, {
          params: {
            cliente_id: formData.cliente,
            tipo_pago: formData.tipo_pago
          }
        })
        
        console.log('Facturas pendientes response:', response.data)
        
        // La respuesta puede venir como { results: [...] } o directamente como array
        if (response.data && Array.isArray(response.data)) {
          return response.data
        } else if (response.data && response.data.results) {
          return response.data.results
        }
        
        return []
      } catch (error) {
        console.error('Error al obtener facturas pendientes:', error)
        console.error('Error response:', error.response?.data)
        throw error
      }
    },
    { 
      enabled: !!formData.cliente && !!formData.tipo_pago,
      retry: 1,
      onError: (error) => {
        console.error('Error en useQuery para facturas pendientes:', error)
        console.error('Error details:', {
          message: error.message,
          response: error.response,
          status: error.response?.status,
          data: error.response?.data
        })
      },
      onSuccess: (data) => {
        console.log('Facturas pendientes cargadas exitosamente:', data)
      }
    }
  )

  const invoices = invoicesData || []

  useEffect(() => {
    if (payment) {
      const forma =
        (payment.forma_pago && payment.forma_pago !== '99')
          ? payment.forma_pago
          : paymentMethodToFormaPago(payment.payment_method || 'TRANSFER')
      const formaSafe = forma === '99' ? '03' : forma
      setFormData({
        tipo_pago: payment.tipo_pago || 'ingreso',
        cliente: payment.cliente || payment.cliente_id || '',
        payment_date: payment.payment_date || getTodayDateOnlyMx(),
        amount: payment.amount || 0,
        reference: payment.reference || '',
        forma_pago: formaSafe,
        payment_method: formaPagoToPaymentMethod(formaSafe),
        bank_account: payment.bank_account || payment.bank_account_id || '',
        estado: payment.estado || 'pendiente',
        moneda: (payment.moneda || 'MXN').toUpperCase(),
        tipo_cambio: payment.tipo_cambio || '',
        aplicaciones: payment.aplicaciones || []
      })
      
      // Cargar aplicaciones existentes
      if (payment.id) {
        api.get(`/financial/aplicaciones-pago/?payment_id=${payment.id}`).then(res => {
          const aplicaciones = res.data.results || res.data || []
          const selected = []
          const amounts = {}
          aplicaciones.forEach(a => {
            const invoiceKey = a.factura_id || a.bill_id
            if (invoiceKey) {
              selected.push(invoiceKey)
              amounts[invoiceKey] = parseFloat(a.monto || 0)
            }
          })
          setSelectedInvoices(selected)
          setInvoiceAmounts(amounts)
        })
      }
    }
  }, [payment])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => {
      const next = { ...prev, [name]: value }
      if (name === 'moneda') {
        const mon = (value || 'MXN').toUpperCase()
        next.moneda = mon
        if (mon === 'MXN') next.tipo_cambio = ''
      }
      return next
    })
    
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleInvoiceToggle = (invoiceId) => {
    const newSelected = selectedInvoices.includes(invoiceId)
      ? selectedInvoices.filter(id => id !== invoiceId)
      : [...selectedInvoices, invoiceId]
    
    setSelectedInvoices(newSelected)
    
    if (!newSelected.includes(invoiceId)) {
      const newAmounts = { ...invoiceAmounts }
      delete newAmounts[invoiceId]
      setInvoiceAmounts(newAmounts)
    }
  }

  const handleInvoiceAmountChange = (invoiceId, amount) => {
    setInvoiceAmounts(prev => ({
      ...prev,
      [invoiceId]: parseFloat(amount) || 0
    }))
  }
  
  const getInvoiceKey = (invoice) => {
    if (invoice.tipo === 'bill') return invoice.bill_id ?? invoice.id
    return invoice.id || invoice.uuid || invoice.invoice_number
  }

  const getInvoiceDisplayFolio = (invoice) => {
    if (!invoice) return 'N/A'
    if (invoice.tipo === 'bill') return invoice.numero || invoice.uuid?.slice(0, 8) || invoice.uuid || 'N/A'
    if (invoice.numero) return getDisplaySerieFolio(invoice.numero)
    if (invoice.invoice_number) return getDisplaySerieFolio(invoice.invoice_number)
    if (invoice.uuid) return invoice.uuid
    return 'N/A'
  }

  const calculateTotal = () => {
    return Object.values(invoiceAmounts).reduce((sum, amount) => sum + parseFloat(amount || 0), 0)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    const newErrors = {}
    if (!formData.cliente) newErrors.cliente = 'Cliente es requerido'
    if (!formData.payment_date) newErrors.payment_date = 'Fecha es requerida'
    if (!formData.amount || formData.amount <= 0) newErrors.amount = 'Monto debe ser mayor a 0'
    
    // Validar cuenta bancaria para transferencia / cheque (02, 03)
    if (formaPagoRequiereCuentaBancaria(formData.forma_pago) && !formData.bank_account) {
      newErrors.bank_account = 'La cuenta bancaria es requerida para transferencias y cheques'
    }
    if (!formData.forma_pago || formData.forma_pago === '99') {
      newErrors.forma_pago = 'Seleccione la forma de pago SAT'
    }

    if ((formData.moneda || 'MXN').toUpperCase() !== 'MXN') {
      const tc = parseFloat(formData.tipo_cambio)
      if (!tc || tc <= 0) {
        newErrors.tipo_cambio = 'Tipo de cambio obligatorio y mayor a 0 cuando la moneda no es MXN'
      }
    }
    
    // Validar que las aplicaciones no excedan el monto del pago
    const totalAplicar = calculateTotal()
    if (selectedInvoices.length > 0 && totalAplicar > parseFloat(formData.amount)) {
      newErrors.aplicaciones = `El total a aplicar ($${totalAplicar.toFixed(2)}) excede el monto del pago ($${parseFloat(formData.amount).toFixed(2)})`
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    
    // Preparar datos del pago (sin aplicaciones)
    const submitData = {
      tipo_pago: formData.tipo_pago,
      cliente: formData.cliente,
      payment_date: formData.payment_date,
      amount: parseFloat(formData.amount),
      reference: formData.reference || '',
      forma_pago: formData.forma_pago,
      payment_method: formaPagoToPaymentMethod(formData.forma_pago),
      bank_account: formData.bank_account || null,
      estado: formData.estado,
      moneda: (formData.moneda || 'MXN').toUpperCase(),
      aplicaciones: selectedInvoices.map(invoiceKey => {
        // Buscar el invoice para determinar si es Invoice o XMLCFDI
        const invoice = invoices.find(inv => getInvoiceKey(inv) === invoiceKey)
        
        if (!invoice) {
          console.error(`No se encontró invoice con key: ${invoiceKey}`)
          return null
        }
        
        // Usar el ID numérico del invoice, no el key
        const idNumero = invoice.id
        
        if (!idNumero || isNaN(idNumero)) {
          console.error(`ID inválido para invoice:`, invoice)
          return null
        }
        
        const monto = parseFloat(invoiceAmounts[invoiceKey] || 0)
        
        if (monto <= 0) {
          console.warn(`Monto inválido para invoice ${idNumero}: ${monto}`)
          return null
        }
        
        if (invoice.tipo === 'bill') {
          return { bill_id: parseInt(invoice.bill_id ?? idNumero, 10), monto }
        }
        return { factura_id: parseInt(idNumero, 10), monto }
      }).filter(app => app !== null) // Filtrar aplicaciones inválidas
    }
    if ((submitData.moneda || 'MXN') !== 'MXN') {
      submitData.tipo_cambio = parseFloat(formData.tipo_cambio) || 0
    }
    
    if (payment) {
      submitData.id = payment.id
    }
    
    onSubmit(submitData)
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Tipo de Pago *</Form.Label>
            <Form.Select
              name="tipo_pago"
              value={formData.tipo_pago}
              onChange={handleChange}
              required
            >
              <option value="ingreso">Ingreso</option>
              <option value="egreso">Egreso</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>Cliente *</Form.Label>
            <Form.Select
              name="cliente"
              value={formData.cliente}
              onChange={handleChange}
              isInvalid={!!errors.cliente}
              required
            >
              <option value="">Seleccionar cliente...</option>
              {clients?.map(client => (
                <option key={client.id} value={client.id}>
                  {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno}`}
                </option>
              ))}
            </Form.Select>
            {errors.cliente && <Form.Control.Feedback type="invalid">{errors.cliente}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Group>
            <Form.Label>Fecha *</Form.Label>
            <Form.Control
              type="date"
              name="payment_date"
              value={formData.payment_date}
              onChange={handleChange}
              isInvalid={!!errors.payment_date}
              required
            />
            {errors.payment_date && <Form.Control.Feedback type="invalid">{errors.payment_date}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Monto *</Form.Label>
            <Form.Control
              type="number"
              min="0"
              step="0.01"
              name="amount"
              value={formData.amount}
              onChange={handleChange}
              isInvalid={!!errors.amount}
              required
            />
            {errors.amount && <Form.Control.Feedback type="invalid">{errors.amount}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group>
            <Form.Label>Forma de Pago *</Form.Label>
            <Form.Select
              name="forma_pago"
              value={formData.forma_pago}
              onChange={(e) => {
                const forma = e.target.value
                const requiereCuenta = formaPagoRequiereCuentaBancaria(forma)
                setFormData(prev => ({
                  ...prev,
                  forma_pago: forma,
                  payment_method: formaPagoToPaymentMethod(forma),
                  // Al dejar de requerirla se limpia: la cuenta capturada NO se
                  // conserva para reponerla si el usuario vuelve a transferencia.
                  // Es el comportamiento que ya habia, y cambiarlo altera el
                  // bank_account que viaja en el payload del pago.
                  bank_account: requiereCuenta ? prev.bank_account : '',
                }))
                // Los errores que este cambio vuelve falsos se van con el.
                // handleChange ya lo hace para el resto de los campos; este
                // onChange no lo hacia, asi que el campo de cuenta bancaria
                // reaparecia en rojo al volver a transferencia sin que el
                // usuario lo hubiera tocado.
                setErrors(prev => {
                  const next = { ...prev }
                  delete next.forma_pago
                  // Si la nueva forma sigue exigiendo cuenta y sigue vacia, el
                  // error sigue siendo cierto: no se toca.
                  if (!requiereCuenta) delete next.bank_account
                  return next
                })
              }}
              isInvalid={!!errors.forma_pago}
              required
            >
              {FORMA_PAGO_SAT_OPTIONS_PAGO.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </Form.Select>
            {errors.forma_pago && <Form.Control.Feedback type="invalid">{errors.forma_pago}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Referencia</Form.Label>
            <Form.Control
              type="text"
              name="reference"
              value={formData.reference}
              onChange={handleChange}
              placeholder="Número de referencia, cheque, etc."
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Moneda del cobro *</Form.Label>
            <Form.Select
              name="moneda"
              value={formData.moneda || 'MXN'}
              onChange={handleChange}
            >
              {(catalogoMonedas?.length
                ? catalogoMonedas
                : [{ codigo: 'MXN', descripcion: 'Peso Mexicano' }, { codigo: 'USD', descripcion: 'Dólar' }]
              ).map((m) => (
                <option key={m.codigo || m.id} value={m.codigo}>
                  {m.codigo}{m.descripcion ? ` — ${m.descripcion}` : ''}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Tipo de cambio</Form.Label>
            <Form.Control
              type="number"
              name="tipo_cambio"
              min="0"
              step="0.000001"
              value={(formData.moneda || 'MXN') === 'MXN' ? '1' : (formData.tipo_cambio || '')}
              onChange={handleChange}
              disabled={(formData.moneda || 'MXN') === 'MXN'}
              isInvalid={!!errors.tipo_cambio}
              placeholder="MXN por 1"
            />
            {errors.tipo_cambio && <Form.Control.Feedback type="invalid">{errors.tipo_cambio}</Form.Control.Feedback>}
          </Form.Group>
        </Col>
      </Row>

      <Row className="mb-3">
        {formaPagoRequiereCuentaBancaria(formData.forma_pago) && (
          <Col md={6}>
            <Form.Group>
              <Form.Label>Cuenta Bancaria *</Form.Label>
              <Form.Select
                name="bank_account"
                value={formData.bank_account || ''}
                onChange={handleChange}
                isInvalid={!!errors.bank_account}
                required
              >
                <option value="">Seleccionar cuenta bancaria...</option>
                {bankAccounts?.map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} - {account.account_number} ({account.bank_name || 'N/A'})
                  </option>
                ))}
              </Form.Select>
              {errors.bank_account && (
                <Form.Control.Feedback type="invalid">{errors.bank_account}</Form.Control.Feedback>
              )}
              <Form.Text className="text-muted">
                Requerido para transferencias y cheques
              </Form.Text>
            </Form.Group>
          </Col>
        )}
        {!formaPagoRequiereCuentaBancaria(formData.forma_pago) && (
          <Col md={6}>
            <Form.Group>
              <Form.Label>Estado</Form.Label>
              <Form.Select
                name="estado"
                value={formData.estado}
                onChange={handleChange}
              >
                <option value="pendiente">Pendiente</option>
                <option value="aplicado">Aplicado</option>
                <option value="cancelado">Cancelado</option>
              </Form.Select>
            </Form.Group>
          </Col>
        )}
      </Row>

      {/* Aplicación a facturas */}
      {formData.cliente && formData.tipo_pago && (
        <div className="mb-3">
          <h5>
            Aplicar a Facturas {formData.tipo_pago === 'ingreso' ? '(Emitidas)' : '(Recibidas)'}
          </h5>
          {loadingInvoices ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm" role="status">
                <span className="visually-hidden">Cargando facturas...</span>
              </div>
              <p className="text-muted mt-2">Buscando facturas pendientes...</p>
            </div>
          ) : invoicesError ? (
            <Alert variant="danger" className="mt-2">
              Error al cargar facturas: {invoicesError.message || 'Error desconocido'}
            </Alert>
          ) : invoices && invoices.length > 0 ? (
            <Table responsive>
              <thead>
                <tr>
                  <th>Seleccionar</th>
                  <th>Número/UUID</th>
                  <th>Fecha Emisión</th>
                  <th>{formData.tipo_pago === 'ingreso' ? 'Receptor' : 'Emisor'}</th>
                  <th>Total</th>
                  <th>Pendiente</th>
                  <th>Monto a Aplicar</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const invoiceKey = getInvoiceKey(invoice)
                  const isSelected = selectedInvoices.includes(invoiceKey)
                  const total = parseFloat(invoice.total || 0)
                  const pagado = parseFloat(invoice.monto_pagado || 0)
                  const pendienteCalculado = invoice.saldo_pendiente || (total - pagado)
                  const pendiente = Math.max(0, pendienteCalculado)
                  
                  // Función para obtener el label y color del status
                  const getStatusBadge = () => {
                    if (invoice.tipo === 'bill') {
                      const s = (invoice.status || 'PENDIENTE').toUpperCase()
                      const map = { PENDIENTE: 'warning', PARCIAL: 'info', PAGADA: 'success', CANCELADA: 'danger' }
                      return { text: s, variant: map[s] || 'secondary' }
                    }
                    const status = invoice.status || 'DRAFT'
                    const statusMap = {
                      'DRAFT': { text: 'Borrador', variant: 'secondary' },
                      'SENT': { text: 'Enviada', variant: 'primary' },
                      'PARTIAL': { text: 'Parcial', variant: 'warning' },
                      'PAID': { text: 'Pagada', variant: 'success' },
                      'CANCEL_PENDING': { text: 'Cancelación en proceso', variant: 'warning' },
                      'CANCELLED': { text: 'Cancelada', variant: 'danger' }
                    }
                    return statusMap[status] || { text: status, variant: 'secondary' }
                  }
                  
                  const statusBadge = getStatusBadge()
                  
                  return (
                    <tr key={invoiceKey}>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              handleInvoiceToggle(invoiceKey)
                              // Si se selecciona, auto-completar con el saldo pendiente
                              if (!isSelected && pendiente > 0) {
                                handleInvoiceAmountChange(invoiceKey, pendiente)
                              }
                            }}
                          />
                          <Badge bg={statusBadge.variant} className="ms-1">
                            {statusBadge.text}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <strong>
                          {getInvoiceDisplayFolio(invoice)}
                        </strong>
                        {invoice.tipo === 'bill' && (
                          <Badge bg="info" className="ms-2">Recibida</Badge>
                        )}
                      </td>
                      <td>
                        {invoice.fecha_emision ? (
                          formatDateOnlyEsMX(invoice.fecha_emision, {}, '—')
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td>
                        {formData.tipo_pago === 'ingreso' ? (
                          invoice.receptor?.nombre || 'N/A'
                        ) : (
                          invoice.emisor?.nombre || 'N/A'
                        )}
                      </td>
                      <td>
                        {formatCurrency(total)}
                      </td>
                      <td>
                        <Badge bg={pendiente === total ? 'warning' : pendiente > 0 ? 'info' : 'success'}>
                          {formatCurrency(pendiente)}
                        </Badge>
                        {pendiente < total && (
                          <small className="text-muted d-block">
                            Pagado: {formatCurrency(pagado)}
                          </small>
                        )}
                      </td>
                      <td>
                        {isSelected && (
                          <Form.Control
                            type="number"
                            min="0"
                            max={pendiente}
                            step="0.01"
                            placeholder={`Máx: ${pendiente.toFixed(2)}`}
                            value={invoiceAmounts[invoiceKey] || pendiente.toFixed(2)}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0
                              const maxValue = Math.min(value, pendiente)
                              handleInvoiceAmountChange(invoiceKey, maxValue)
                            }}
                            onBlur={(e) => {
                              // Auto-ajustar al máximo si excede
                              const value = parseFloat(e.target.value) || 0
                              if (value > pendiente) {
                                handleInvoiceAmountChange(invoiceKey, pendiente)
                              }
                            }}
                            size="sm"
                            isInvalid={invoiceAmounts[invoiceKey] && parseFloat(invoiceAmounts[invoiceKey]) > pendiente}
                          />
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          ) : (
            <Alert variant="info" className="mt-2">
              No hay facturas pendientes para este tipo de pago ({formData.tipo_pago === 'ingreso' ? 'Emitidas' : 'Recibidas'}).
            </Alert>
          )}
          {selectedInvoices.length > 0 && (
            <Alert 
              variant={calculateTotal() > parseFloat(formData.amount || 0) ? 'danger' : 'info'} 
              className="mt-2"
            >
              <strong>Total a aplicar:</strong> {formatCurrency(calculateTotal())} / Monto del pago: {formatCurrency(formData.amount || 0)}
              {calculateTotal() > parseFloat(formData.amount || 0) && (
                <div className="mt-1 text-danger">
                  <small>⚠ El total a aplicar excede el monto del pago</small>
                </div>
              )}
              {calculateTotal() < parseFloat(formData.amount || 0) && calculateTotal() > 0 && (
                <div className="mt-1 text-muted">
                  <small>Monto disponible sin aplicar: {formatCurrency((parseFloat(formData.amount || 0) - calculateTotal()))}</small>
                </div>
              )}
            </Alert>
          )}
          {errors.aplicaciones && (
            <Alert variant="danger" className="mt-2">
              {errors.aplicaciones}
            </Alert>
          )}
        </div>
      )}

      <div className="d-flex justify-content-end gap-2">
        <Button variant="secondary" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" disabled={isLoading}>
          {isLoading ? 'Guardando...' : (payment ? 'Actualizar' : 'Crear')}
        </Button>
      </div>
    </Form>
  )
}

export default PaymentForm

